import React, { useCallback, useEffect, useState } from 'react';
import type { Api } from '../api';
import type { ClientDto, ClientInput, UserDto, Weekday } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { useNavState } from '../navState';
import { Icon } from '../icons';
import { WEEKDAY_LABELS, euroInput, minuteToTime, parseEuro, rateShort, timeToMinute } from '../format';
import { Avatar, Empty, ErrorBanner, Field, More, Panel, Pill, Row, Section, Toggle } from '../ui';
import { UsersManager } from './UsersManager';
import { TimeInput } from '../pickers';

const ALL_DAYS: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/** Anagrafica clienti: elenco a sinistra, scheda (tariffe, soglie, commerciali, accessi) a destra. */
export function ClientsPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const [clients, setClients] = useState<ClientDto[]>([]);
  const [agents, setAgents] = useState<UserDto[]>([]);
  const [contacts, setContacts] = useState<UserDto[]>([]);
  // Arrivo dal Percorso clienti: trattativa vinta da trasformare in scheda cliente
  const fromDeal = (ctx.pageParam as { newClient?: NewClientPrefill } | null)?.newClient ?? null;
  const [selected, setSelected] = useNavState<number | 'new' | null>(ctx, 'client', fromDeal ? 'new' : ((ctx.pageParam as { open?: number } | null)?.open ?? null));
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [accessOpen, setAccessOpen] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [c, a, u] = await Promise.all([
      api.get<ClientDto[]>('/api/admin/clients'),
      api.get<UserDto[]>('/api/admin/users?role=agent'),
      api.get<UserDto[]>('/api/admin/users?role=client'),
    ]);
    setClients(c);
    setAgents(a);
    setContacts(u);
  }, [api]);

  useEffect(() => {
    load().catch((e) => setError((e as Error).message));
  }, [load]);

  const q = search.trim().toLowerCase();
  const visible = clients.filter((c) => (showInactive || c.active) && (!q || c.name.toLowerCase().includes(q)));
  const selectedClient = typeof selected === 'number' ? (clients.find((c) => c.id === selected) ?? null) : null;
  const contactNames = (clientId: number) =>
    contacts
      .filter((u) => u.clientId === clientId && u.active)
      .map((u) => u.name)
      .join(', ');

  return (
    <div className="panels">
      <Panel
        title="Clienti"
        subtitle="Tariffe orarie, provvigioni e accessi"
        right={
          <button type="button" className="btn small dark" title="Crea un cliente nuovo" onClick={() => setSelected('new')}>
            <Icon name="plus" size={16} /> Nuovo
          </button>
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <label className="add-input">
          <Icon name="search" size={18} />
          <input placeholder="Cerca cliente…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        {visible.length === 0 ? (
          <Empty icon="building">Nessun cliente. Premi "Nuovo" per crearne uno.</Empty>
        ) : (
          visible.map((c) => (
            <Row key={c.id} onClick={() => setSelected(c.id)} active={selected === c.id}>
              <Avatar name={c.name} />
              <div className="grow">
                <div className="title">{c.name}</div>
                <div className="sub">
                  {rateShort(c)}
                  {contactNames(c.id) ? ` · ${contactNames(c.id)}` : ' · nessun accesso'}
                </div>
              </div>
              {!c.active && <Pill tone="bad">disattivato</Pill>}
              <Icon name="chevronRight" size={16} className="muted" />
            </Row>
          ))
        )}
        <div className="hstack between" style={{ padding: '4px 8px' }}>
          <span className="muted small">Mostra i clienti disattivati</span>
          <Toggle checked={showInactive} onChange={setShowInactive} />
        </div>
      </Panel>

      <Panel
        title={selected === 'new' ? 'Nuovo cliente' : selectedClient ? selectedClient.name : 'Scheda cliente'}
        subtitle={selectedClient ? rateShort(selectedClient) : selected === 'new' ? 'Tariffe, soglie e commerciali' : undefined}
      >
        {selected === null && <Empty icon="edit">Seleziona un cliente a sinistra oppure creane uno nuovo.</Empty>}
        {(selected === 'new' || selectedClient) && (
          <ClientForm
            key={selected === 'new' ? 'new' : selectedClient!.id}
            api={api}
            client={selectedClient}
            prefill={selected === 'new' ? fromDeal : null}
            agents={agents}
            onSaved={(saved) => {
              if (selected === 'new' && fromDeal?.dealId) void api.post(`/api/deals/${fromDeal.dealId}/client`, { clientId: saved.id }).catch(() => undefined);
              setClients((list) => (list.some((c) => c.id === saved.id) ? list.map((c) => (c.id === saved.id ? saved : c)) : [...list, saved]));
              setSelected(saved.id);
            }}
          />
        )}
        {selectedClient && (
          <Section
            title="Accessi del cliente"
            count={contacts.filter((u) => u.clientId === selectedClient.id).length}
            open={accessOpen}
            onToggle={() => setAccessOpen((o) => !o)}
          >
            <div className="muted small">Chi del cliente può entrare nel CRM: vede quando lavori per lui e i report.</div>
            <UsersManager api={api} role="client" clientId={selectedClient.id} users={contacts.filter((u) => u.clientId === selectedClient.id)} onChanged={load} />
          </Section>
        )}
      </Panel>
    </div>
  );
}

interface NewClientPrefill {
  name: string;
  phones?: string | null;
  referrerUserId?: number | null;
  managerUserId?: number | null;
  dealId?: number | null;
}

function ClientForm({ api, client, prefill, agents, onSaved }: { api: Api; client: ClientDto | null; prefill?: NewClientPrefill | null; agents: UserDto[]; onSaved: (c: ClientDto) => void }) {
  const [form, setForm] = useState(() => ({
    name: client?.name ?? prefill?.name ?? '',
    notes: client?.notes ?? '',
    phones: client?.phones ?? prefill?.phones ?? '',
    hourlyRate: client ? euroInput(client.hourlyRateCents) : '',
    extraRate: euroInput(client?.extraHourlyRateCents),
    thresholdHours: client?.dailyThresholdMinutes != null ? String(client.dailyThresholdMinutes / 60).replace('.', ',') : '',
    workingDays: (client?.workingDays ?? ['mon', 'tue', 'wed', 'thu', 'fri']) as Weekday[],
    nightFrom: minuteToTime(client?.nightFromMinute),
    nightTo: minuteToTime(client?.nightToMinute),
    referrerUserId: client?.referrerUserId ? String(client.referrerUserId) : prefill?.referrerUserId ? String(prefill.referrerUserId) : '',
    referralPct: String(client?.referralPct ?? 12),
    managerUserId: client?.managerUserId ? String(client.managerUserId) : prefill?.managerUserId ? String(prefill.managerUserId) : '',
    managementPct: String(client?.managementPct ?? 5),
    active: client?.active ?? true,
  }));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((f) => ({ ...f, [key]: value }));
  const toggleDay = (d: Weekday) =>
    set('workingDays', form.workingDays.includes(d) ? form.workingDays.filter((x) => x !== d) : [...form.workingDays, d]);
  const pct = (v: string) => {
    const n = Number(v.replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const hourlyRateCents = parseEuro(form.hourlyRate);
    if (hourlyRateCents == null) return setError('Tariffa base non valida');
    const extra = form.extraRate.trim() ? parseEuro(form.extraRate) : null;
    if (form.extraRate.trim() && extra == null) return setError('Tariffa extra non valida');
    const thresholdHours = form.thresholdHours.trim() ? Number(form.thresholdHours.replace(',', '.')) : null;
    if (thresholdHours != null && (!Number.isFinite(thresholdHours) || thresholdHours < 0)) return setError('Soglia ore non valida');
    const nightFrom = form.nightFrom.trim() ? timeToMinute(form.nightFrom) : null;
    const nightTo = form.nightTo.trim() ? timeToMinute(form.nightTo) : null;
    if ((form.nightFrom.trim() && nightFrom == null) || (form.nightTo.trim() && nightTo == null))
      return setError('Fascia notturna non valida');
    if ((nightFrom == null) !== (nightTo == null)) return setError('Indica sia inizio sia fine della fascia notturna');
    const input: ClientInput = {
      name: form.name.trim(),
      notes: form.notes.trim() || null,
      phones: form.phones.trim() || null,
      hourlyRateCents,
      extraHourlyRateCents: extra,
      dailyThresholdMinutes: thresholdHours == null ? null : Math.round(thresholdHours * 60),
      workingDays: form.workingDays,
      nightFromMinute: nightFrom,
      nightToMinute: nightTo,
      referrerUserId: form.referrerUserId ? Number(form.referrerUserId) : null,
      referralPct: form.referrerUserId ? pct(form.referralPct) : 0,
      managerUserId: form.managerUserId ? Number(form.managerUserId) : null,
      managementPct: form.managerUserId ? pct(form.managementPct) : 0,
      active: form.active,
    };
    setBusy(true);
    setError(null);
    try {
      const result = client ? await api.put<ClientDto>(`/api/admin/clients/${client.id}`, input) : await api.post<ClientDto>('/api/admin/clients', input);
      onSaved(result);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const agentOptions = (selected: string) => agents.filter((a) => a.active || String(a.id) === selected);

  return (
    <form onSubmit={save} className="stack" style={{ gap: 4 }}>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <div className="fields-2">
        <Field label="Nome cliente / azienda">
          <input value={form.name} onChange={(e) => set('name', e.target.value)} required autoFocus={!client} />
        </Field>
        <Field label="Tariffa base (€/h)">
          <input value={form.hourlyRate} onChange={(e) => set('hourlyRate', e.target.value)} required inputMode="decimal" placeholder="es. 60" />
        </Field>
      </div>
      <Field label="Telefoni" hint="Separati da virgola: le chiamate con questi numeri contano come lavoro (app sul telefono).">
        <input value={form.phones} onChange={(e) => set('phones', e.target.value)} inputMode="tel" placeholder="es. +39 333 1234567, 06 1234567" />
      </Field>
      <Field label="Stato">
        <div className="hstack" style={{ padding: '2px 0' }}>
          <Toggle checked={form.active} onChange={(v) => set('active', v)} />
          <span className="small">{form.active ? 'Attivo' : 'Disattivato: non compare in «Ore di lavoro»'}</span>
        </div>
      </Field>
      <More
        label="Tariffa extra, orari e note"
        open={!!(form.extraRate || form.thresholdHours || form.nightFrom || form.nightTo || form.notes || form.workingDays.length !== 5)}
      >
        <div className="fields-2">
          <Field label="Tariffa extra (€/h)" hint="Dopo la soglia del giorno e nei giorni di riposo. Vuoto = sempre tariffa base.">
            <input value={form.extraRate} onChange={(e) => set('extraRate', e.target.value)} inputMode="decimal" placeholder="es. 28" />
          </Field>
          <Field label="Soglia ore/giorno a tariffa base" hint="Vuoto = nessuna soglia.">
            <input value={form.thresholdHours} onChange={(e) => set('thresholdHours', e.target.value)} inputMode="decimal" placeholder="es. 5" />
          </Field>
          <Field label="Fascia notturna a tariffa extra" hint="Vuoto = nessuna fascia notturna. Es. 20:00 - 08:00.">
            <div className="hstack" style={{ gap: 8 }}>
              <TimeInput value={form.nightFrom} onChange={(v) => set('nightFrom', v)} from={0} to={23} step={30} />
              <span className="muted small">alle</span>
              <TimeInput value={form.nightTo} onChange={(v) => set('nightTo', v)} from={0} to={23} step={30} />
            </div>
          </Field>
          <Field label="Giorni lavorativi">
            <div className="checks" style={{ padding: '4px 0' }}>
              {ALL_DAYS.map((d) => (
                <label key={d} className={form.workingDays.includes(d) ? 'on' : ''}>
                  <input type="checkbox" checked={form.workingDays.includes(d)} onChange={() => toggleDay(d)} />
                  {WEEKDAY_LABELS[d]}
                </label>
              ))}
            </div>
          </Field>
        </div>
        <Field label="Note interne">
          <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} style={{ minHeight: 70 }} />
        </Field>
      </More>
      <More label="Commerciale e provvigioni" open={!!(form.referrerUserId || form.managerUserId)}>
        <div className="fields-2">
          <Field label="Segnalato da">
            <select value={form.referrerUserId} onChange={(e) => set('referrerUserId', e.target.value)}>
              <option value="">— nessuno —</option>
              {agentOptions(form.referrerUserId).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="% segnalazione" hint="Ricorrente su tutto il fatturato del cliente.">
            <input value={form.referralPct} onChange={(e) => set('referralPct', e.target.value)} inputMode="decimal" disabled={!form.referrerUserId} />
          </Field>
          <Field label="Gestito da">
            <select value={form.managerUserId} onChange={(e) => set('managerUserId', e.target.value)}>
              <option value="">— nessuno —</option>
              {agentOptions(form.managerUserId).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="% gestione" hint="Ricorrente finché segue il cliente.">
            <input value={form.managementPct} onChange={(e) => set('managementPct', e.target.value)} inputMode="decimal" disabled={!form.managerUserId} />
          </Field>
        </div>
      </More>
      <div className="hstack" style={{ justifyContent: 'flex-end' }}>
        <button type="submit" className="btn dark" disabled={busy}>
          <Icon name={saved ? 'check' : 'check'} size={16} /> {saved ? 'Salvato' : client ? 'Salva modifiche' : 'Crea cliente'}
        </button>
      </div>
    </form>
  );
}
